/**
 * useAiAsk — debounced **streaming** agentic call to
 * `POST /api/ai/ask/stream`.
 *
 * The endpoint runs a ReAct-style LLM loop server-side and emits
 * one NDJSON line per step. We surface those steps in real-time
 * via `steps` so the UI can show "Looking up payments… → Found 3 →
 * Drafting answer" instead of an opaque spinner. The final event
 * carries the same `{ answer, results, actions, proposals,
 * navigate }` shape the legacy non-streaming endpoint returned.
 *
 * We fire it 700ms after the user stops typing (slightly slower than
 * the old parse-command 600ms because the agent loop is heavier and
 * we want to avoid bursts during fast typing). Short queries skip the
 * round-trip — same logic as the legacy parser.
 *
 * Returns:
 *   answer    – string|null      (top-of-pane response)
 *   results   – array              (clickable item refs)
 *   actions   – array              (mutation chips with kind+id+label)
 *   proposals – array              (disambiguation chips)
 *   navigate  – string|null      (auto-navigate target)
 *   loading   – boolean            (in-flight)
 *   error     – string|null      (one-shot user-facing error)
 *   steps     – array              (per-step progress labels — see
 *                                   below for shape)
 *   clear()                        (reset + cancel in-flight stream)
 *
 * `steps` shape: array of `{ label: string, status: 'active'|'done',
 * step: number }`. The most recent step is `active`; everything
 * before it is `done`. After the final event lands, every step
 * flips to `done`. The panel renders them as a thin vertical list
 * with check marks + a spinner on the active one.
 *
 * Note: opts out of experimental React Compiler lint rules
 * (react-hooks/set-state-in-effect). The project ESLint config does
 * not enable these.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import api from '../../api';
import { logger } from '../../utils/logger';
import { getOrCreateAiSessionId } from '../../utils/aiSession';
import { streamAiAsk } from './streamAiAsk';

/**
 * formatStepSummary — turn the backend's `summary` payload into a
 * one-line UI suffix for a completed step.
 *
 * Backend shapes (from `_agent_helpers.summarise_result`):
 *   {items: N}           — query_items returned N items
 *   {rows: N, groupBy?}  — aggregate_payments returned N groups
 *   {mutated: "kind:id"} — mark_done/snooze/create_item ran
 *   {error: "..."}       — tool errored (LLM will recover)
 *   {ok: true}           — fallback / unstructured success
 *
 * Returns a short string like "Found 3" / "Updated" / "Errored" /
 * "" (empty → no suffix renders). Pluralisation handled where it
 * matters; everything else stays terse on purpose so the line
 * doesn't wrap on narrow command-bar widths.
 */
function formatStepSummary(summary) {
  if (!summary || typeof summary !== 'object') return '';
  if (typeof summary.items === 'number') {
    return summary.items === 0 ? 'None found' : `Found ${summary.items}`;
  }
  if (typeof summary.rows === 'number') {
    return summary.rows === 0 ? 'No rows' : `Found ${summary.rows}`;
  }
  if (typeof summary.mutated === 'string') return 'Updated';
  if (summary.error) return 'Errored';
  return '';
}

const DEBOUNCE_MS = 700;
const MIN_QUERY_LEN = 5;

export function useAiAsk({ query, enabled }) {
  const [answer, setAnswer] = useState(null);
  const [results, setResults] = useState([]);
  const [actions, setActions] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [navigate, setNavigate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Per-step progress labels — populated in real-time as the
  // backend stream emits `step` events. Renders as a small "✓ Did
  // X · Now doing Y…" line in the panel during loading, so users
  // perceive a smart multi-step assistant instead of one opaque
  // spinner. Cleared on every new request and on clear().
  const [steps, setSteps] = useState([]);
  // Mirror of `mutationLockRef.current` exposed to the UI so the
  // panel can show "Clear input to ask again" once a mutation has
  // landed. Kept in a separate state because refs don't trigger
  // re-renders.
  const [locked, setLocked] = useState(false);
  // Track the in-flight query so a fast-typing user's older
  // responses can't clobber the latest.
  const requestIdRef = useRef(0);
  // AbortController for the current in-flight stream — set when a
  // request kicks off, used to cancel when a newer query supersedes
  // it (or the user clears the input).
  const abortRef = useRef(null);
  // Lazy-init the session id once per hook mount. The helper is
  // idempotent across mounts (uses localStorage) so multiple
  // useAiAsk consumers all share the same conversation thread.
  const sessionIdRef = useRef(null);
  if (sessionIdRef.current === null) {
    sessionIdRef.current = getOrCreateAiSessionId();
  }
  // The last query string we ACTUALLY sent to /api/ai/ask. We dedupe
  // against this so a user adding a space (or any text that ends up
  // identical after trim) cannot re-fire the agent and create a
  // duplicate task/call/payment.
  const lastSentQueryRef = useRef('');
  // After ANY mutation completes (actions or proposals returned with
  // mutation-class items), we LOCK auto-fires until the user clears
  // the input. This stops the "I added 'about deck' to my call query
  // → second call got created" foot-gun. The user can still trigger
  // a new query — they just have to clear the input first.
  const mutationLockRef = useRef(false);

  const clear = useCallback(() => {
    requestIdRef.current += 1;
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    lastSentQueryRef.current = '';
    mutationLockRef.current = false;
    setLocked(false);
    setAnswer(null);
    setResults([]);
    setActions([]);
    setProposals([]);
    setNavigate(null);
    setError(null);
    setSteps([]);
    setLoading(false);
  }, []);

  useEffect(() => {
    const q = (query || '').trim();
    if (!enabled || q.length < MIN_QUERY_LEN) {
      clear();
      return undefined;
    }
    // Dedup — never re-fire for the same exact trimmed query string.
    if (q === lastSentQueryRef.current) return undefined;
    // Post-mutation safety lock — refuse to auto-fire after a
    // mutation completed in this session. User must clear input.
    if (mutationLockRef.current) return undefined;

    const myId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    // Seed an immediate "Thinking…" step so the user sees a labelled
    // row from the very first frame after the debounce fires. Real
    // step events from the stream REPLACE this seed (we detect the
    // seed by `step === -1`). Without this, fast LLM responses leave
    // the user staring at a generic spinner for the entire duration.
    setSteps([{ label: 'Thinking…', status: 'active', step: -1 }]);

    const timer = setTimeout(async () => {
      // Cancel any prior in-flight stream before kicking off a new
      // one. Aborts cause `streamAiAsk` to throw, which we swallow
      // when it's superseded (myId !== current).
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        lastSentQueryRef.current = q;
        const finalPayload = await streamAiAsk({
          query: q,
          // Always send the stable session id (when localStorage is
          // available) so the backend can replay conversation
          // memory. Null is sent for private-mode users and is safe —
          // backend short-circuits to no-replay in that case.
          sessionId: sessionIdRef.current,
          signal: controller.signal,
          onEvent: (event) => {
            // Drop stale events from a superseded query — guards
            // against the "fast typer" race where chunks from the
            // previous query land while the new one is starting.
            if (myId !== requestIdRef.current) return;
            // `flushSync` forces React to commit each NDJSON event's
            // state update IMMEDIATELY instead of batching the whole
            // stream into one render. Without this, React 18's
            // automatic batching collapses the entire step → result
            // → step → final sequence into a single render and the
            // user never sees the intermediate "Found N" beats.
            // We accept the small perf cost (one render per event,
            // typically 4–8 per query) because the entire point of
            // streaming is per-step visibility.
            flushSync(() => {
              if (event.type === 'step') {
              // Mark the previous step as done (if any) so the UI can
              // render past steps with a check + the current one with
              // a spinner. We mutate via a functional update to avoid
              // racing against React's batching of multiple events.
              setSteps((prev) => {
                // Drop the synthetic "Thinking…" seed (step === -1) as
                // soon as the first real event arrives — the backend's
                // own labels are always more informative.
                const filtered = prev.filter((s) => s.step !== -1);
                const next = filtered.map((s, i) =>
                  (i === filtered.length - 1 && s.status === 'active')
                    ? { ...s, status: 'done' }
                    : s,
                );
                return [...next, { label: event.label, status: 'active', step: event.step }];
              });
            } else if (event.type === 'result') {
              // Result events confirm the just-finished step. Attach a
              // friendly summary suffix derived from the backend's
              // `summary` shape (`items`/`rows`/`mutated`/`error`/`ok`)
              // so the rendered row reads as
              //     ✓ Looking up payments… · Found 3
              // instead of a bare check mark. Tiny transparency win.
              setSteps((prev) => prev.map((s, i) =>
                (i === prev.length - 1)
                  ? { ...s, status: 'done', summary: formatStepSummary(event.summary) }
                  : s,
              ));
            } else if (event.type === 'error') {
              setError(event.message || 'AI service hiccuped');
            }
            });
          },
        });
        if (myId !== requestIdRef.current) return; // superseded
        // `final` event drives the same state population as the
        // legacy non-streaming response — same shape, same renderers.
        setAnswer(finalPayload.answer || null);
        setResults(Array.isArray(finalPayload.results) ? finalPayload.results : []);
        setActions(Array.isArray(finalPayload.actions) ? finalPayload.actions : []);
        setProposals(Array.isArray(finalPayload.proposals) ? finalPayload.proposals : []);
        setNavigate(finalPayload.navigate || null);
        // If the agent executed a mutation (server-side `actions`
        // populated) lock further auto-fires until clear(). Proposals
        // alone do NOT lock — they still need user click to mutate.
        if (Array.isArray(finalPayload.actions) && finalPayload.actions.length > 0) {
          mutationLockRef.current = true;
          setLocked(true);
        }
        // Mark all steps done now that the final payload landed.
        setSteps((prev) => prev.map((s) => ({ ...s, status: 'done' })));
      } catch (err) {
        // AbortError from a superseded request — silent.
        if (err?.name === 'AbortError') return;
        if (myId !== requestIdRef.current) return;
        logger.warn('ai/ask/stream failed:', err?.message);
        setError(err?.message || 'AI service hiccuped');
        setAnswer(null);
        setResults([]);
        setProposals([]);
      } finally {
        if (myId === requestIdRef.current) {
          setLoading(false);
          abortRef.current = null;
        }
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, enabled, clear]);

  /**
   * confirmProposal — user clicked a "Did you mean…?" chip. Hits the
   * deterministic confirm endpoint, then optimistically removes the
   * proposal row from local state and surfaces the action as a
   * post-mutation chip in `actions`.
   */
  const confirmProposal = useCallback(async (proposal) => {
    if (!proposal) return null;
    try {
      const resp = await api.post('/ai/confirm', {
        kind: proposal.kind,
        id: proposal.id,
        action: proposal.action,
        params: proposal.params || null,
      });
      const ok = resp?.data || {};
      // Remove this proposal from the chip row and surface a green
      // "✓ <action> done" chip in the actions strip.
      setProposals((prev) => prev.filter((p) => !(p.kind === proposal.kind && p.id === proposal.id)));
      setActions((prev) => [...prev, {
        kind: proposal.kind,
        id: proposal.id,
        label: ok.answer || `${proposal.action} done`,
      }]);
      // Lock further auto-fires — a manual chip click is still a
      // mutation, so the same "clear input to ask again" rules apply.
      mutationLockRef.current = true;
      setLocked(true);
      if (ok.answer) setAnswer(ok.answer);
      return ok;
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not confirm action';
      setError(detail);
      return null;
    }
  }, []);

  /**
   * confirmAllProposals — user clicked the "Apply to all N" batch chip.
   * Sends every visible proposal to the batch endpoint, then collapses
   * the chip row into a single summary action chip on success. If a
   * subset fails server-side, the surviving proposals stay clickable
   * so the user can retry individually.
   */
  const confirmAllProposals = useCallback(async () => {
    if (!proposals || proposals.length === 0) return null;
    try {
      const payload = proposals.map((p) => ({
        kind: p.kind, id: p.id, action: p.action, params: p.params || null,
      }));
      const resp = await api.post('/ai/confirm-batch', { proposals: payload });
      const ok = resp?.data || {};
      // Drop the succeeded ones; keep failures so the user can retry.
      const failedIds = new Set(
        (ok.outcomes || []).filter((o) => !o.ok).map((o) => `${o.kind}:${o.id}`),
      );
      setProposals((prev) => prev.filter((p) => failedIds.has(`${p.kind}:${p.id}`)));
      setActions((prev) => [...prev, {
        kind: 'batch',
        id: 'batch',
        label: ok.answer || `Applied to ${ok.succeeded}/${ok.succeeded + ok.failed}`,
      }]);
      if (ok.answer) setAnswer(ok.answer);
      return ok;
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not apply to all';
      setError(detail);
      return null;
    }
  }, [proposals]);

  return { answer, results, actions, proposals, navigate, loading, error, locked, steps, clear, confirmProposal, confirmAllProposals };
}
